import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { clientService } from "../../../../../../api";
import { Button, InfoBlock, ModalVariables, TopProgress, Typography } from "../../../../../../components";
import { useUi } from "../../../../../../context/ui.context";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import {
  PageContainer,
  DetailsContent,
  InfoSection,
  CreditSection,
  CreditHeader,
  CreditMeta,
  CreditList,
  CreditRow,
  CreditValue,
  CreditActions,
  IconButton,
  ConfigurationLink,
} from "../styles";
import { icons } from "../../../../../../icons";

const Details: FC = () => {
  const [t] = useTranslation();
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const { userId } = useParams();
  const [info, setInfo] = useState<any[]>([]);
  const [clientData, setClientData] = useState<any>(null);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalData, setModalData] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalComplete, setModalComplete] = useState<boolean>(false);
  const [usageUpdating, setUsageUpdating] = useState<boolean>(false);
  const { viewAlert } = useUi();

  const openConfigurationModal = useCallback(
    (data: any) => {
      setModalType("change-client-configuration");
      setModalTitle(`${
        t("CHANGE_CONFIGURATION_TITLE", {
          client: data?.long_name || data?.name || "",
        })
      }`);
      setModalData({
        currentConfiguration: data?.openstack_billing_plans?.find(
          (plan: any) => plan?.id === data?.configuration_id
        )?.name,
        options: (data?.openstack_billing_plans || []).map((plan: any) => ({
          value: plan.id,
          label: plan.name,
        })),
      });
      setViewModal(true);
      setActiveAnimation(true);
    },
    [t]
  );

  const buildInfo = useCallback(
    (data: any) => {
      const configurationPlan = data?.openstack_billing_plans?.find(
        (plan: any) => plan?.id === data?.configuration_id
      );
      return [
        { title: t("COUNTRY"), value: data?.country_name || "-" },
        { title: t("DEFAULT_CURRENCY"), value: data?.currency || "-" },
        {
          title: t("CITY"),
          value: [data?.city, data?.state].filter(Boolean).join(", ") || "-",
        },
        {
          title: t("ADDRESS"),
          value:
            [data?.address1, data?.address2]
              .filter((part: string) => part && part.trim().length > 0)
              .join(", ") || "-",
        },
        { title: t("ZIP_CODE"), value: data?.zip_code || "-" },
        { title: t("PHONE"), value: data?.phone || "-" },
        { title: t("ID"), value: data?.id || "-" },
        {
          title: t("CREATED_AT"),
          value: data?.date_created
            ? timeConverterMMDDYYHHMM(data.date_created)
            : "-",
        },
        { title: t("CLIENT_GROUP"), value: data?.group_name || "-" },
        {
          title: t("CLIENT_IS_ON_A_BILLING"),
          value: data?.has_billing_agreement ? t("YES") : t("NO"),
        },
        {
          title: t("CONFIGURATION"),
          value: "",
          custom: (
            <ConfigurationLink onClick={() => openConfigurationModal(data)}>
              {configurationPlan?.name || "-"}
              <img src={icons.Edit} alt={`${t("EDIT")}`} />
            </ConfigurationLink>
          ),
        },
      ];
    },
    [openConfigurationModal, t]
  );

  const fetchClientDetails = useCallback(
    async (silent?: boolean) => {
      if (!userId) return;
      if (!silent) {
        setPageLoading(true);
      }
      try {
        const res = await clientService.getClient(userId);
        setClientData(res.data);
        setInfo(buildInfo(res.data));
      } catch (e: any) {
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: "error",
            message: e?.response?.data?.detail,
          });
        }
      } finally {
        setPageLoading(false);
      }
    },
    [buildInfo, userId, viewAlert]
  );

  useEffect(() => {
    fetchClientDetails();
  }, [fetchClientDetails]);

  const formatAmount = useCallback((value: any, currency?: string) => {
    if (value === undefined || value === null || currency === undefined) {
      return "-";
    }
    const amountNumber = Number(value);
    if (Number.isNaN(amountNumber)) {
      return "-";
    }
    return `${amountNumber.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  }, []);

  const closeModalCancel = useCallback(() => {
    setViewModal(false);
    setActiveAnimation(false);
    setModalType(null);
    setModalTitle("");
    setModalData(null);
    setModalLoading(false);
    setModalComplete(false);
  }, []);

  const handleModalSuccess = useCallback(
    async (payload: any) => {
      if (!modalType || !userId) return;
      setModalLoading(true);
      try {
        if (modalType === "edit-client-credit") {
          await clientService.changeClientCredit(userId, {
            ...payload,
            amount: Number(payload?.amount),
          });
          viewAlert({
            severity: "success",
            message: t("CLIENT_CREDIT_UPDATED"),
          });
        }
        if (modalType === "change-client-configuration") {
          await clientService.changeClientConfiguration(userId, {
            configuration: payload?.configuration,
            id: userId,
          });
          viewAlert({
            severity: "success",
            message: t("CLIENT_CONFIGURATION_UPDATED"),
          });
        }
        setModalComplete(true);
        await fetchClientDetails(true);
        setTimeout(() => {
          closeModalCancel();
        }, 400);
      } catch (e: any) {
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: "error",
            message: e?.response?.data?.detail,
          });
        }
        setModalComplete(false);
      } finally {
        setModalLoading(false);
      }
    },
    [closeModalCancel, fetchClientDetails, modalType, t, userId, viewAlert]
  );

  const openCreditModal = (credit: any) => {
    if (!clientData) return;
    setModalType("edit-client-credit");
    setModalTitle(`${
      t("EDIT_CREDIT_TITLE", {
        client: clientData?.long_name || clientData?.name || "",
      })}`
    );
    setModalData({
      currency: credit?.currency,
      clientName: clientData?.long_name || clientData?.name || "",
    });
    setViewModal(true);
    setActiveAnimation(true);
  };


  const handleUpdateUsage = async () => {
    if (!userId) return;
    setUsageUpdating(true);
    try {
      await clientService.updateClientUsage(userId);
      viewAlert({
        severity: "success",
        message: t("CLIENT_USAGE_UPDATED"),
      });
      await fetchClientDetails(true);
    } catch (e: any) {
      if (e?.response?.data?.detail) {
        viewAlert({
          severity: "error",
          message: e?.response?.data?.detail,
        });
      }
    } finally {
      setUsageUpdating(false);
    }
  };

  const credits = useMemo(() => clientData?.credits || [], [clientData]);

  return (
    <PageContainer style={{ flexDirection: "row", gap: "10px" }}>
      <TopProgress loading={pageLoading} />
      {pageLoading ? null : (
        <DetailsContent>
          <InfoSection>
            <InfoBlock data={info} title={t("CLIENT_INFO")} />
          </InfoSection>
          <CreditSection>
            <CreditHeader>
              <div>
                <Typography variant="h3">{t("CLIENT_CREDIT")}</Typography>
                <CreditMeta>
                  <Typography variant="body">
                    {t("UP_TO_DATE_CREDIT")}:{" "}
                    {formatAmount(clientData?.uptodate_credit, clientData?.currency)}
                  </Typography>
                </CreditMeta>
              </div>
              <Button
                variant="stroke"
                size="mid"
                icon={icons.Restart}
                loading={usageUpdating}
                onClick={handleUpdateUsage}
                title={t("UPDATE_USAGE")}
                customStyles={{ height: 36 }}
              />
            </CreditHeader>
            <CreditList>
              {credits.length === 0 ? (
                <Typography variant="body">{t("NO_DATA")}</Typography>
              ) : (
                credits.map((credit: any) => (
                  <CreditRow key={`${credit.currency}-${credit.id}`}>
                    <div>
                      <Typography variant="body">
                        {t("CREDIT")} ({credit.currency})
                      </Typography>
                      <CreditValue>
                        {formatAmount(credit.amount, credit.currency)}
                      </CreditValue>
                    </div>
                    <CreditActions>
                      <IconButton onClick={() => openCreditModal(credit)}>
                        <img src={icons.Edit} alt={`${t("EDIT")}`} />
                      </IconButton>
                    </CreditActions>
                  </CreditRow>
                ))
              )}
            </CreditList>
          </CreditSection>
        </DetailsContent>
      )}
      <ModalVariables
        modalType={modalType as any}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={handleModalSuccess}
        closeModalCancel={closeModalCancel}
        alertTitle={modalTitle}
        dataToEdit={modalData}
        loading={modalLoading}
        loadingComplete={modalComplete}
      />
    </PageContainer>
  );
};

export default Details;
